import { EmptyState, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ondit/ui";
import { useQuery } from "@tanstack/react-query";
import { api } from "~/core/api/index";
import { useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Async } from "~/core/ui/states";

/**
 * What an article for a gap would be written from: the fact table, its
 * sources, and the generator's refusal if it would refuse.
 *
 * The facts are a table because that is the shape the check pass reads them
 * in afterwards; a person looking at a page that came out wrong should see the
 * same rows the checker had. The refusal is shown before the button that
 * spends, which is the reason the brief is shown at all.
 */
export function GapBrief({
	entityId,
	gapId,
	recipe,
	otherEntityId,
	locale,
}: {
	entityId: string;
	gapId: string;
	recipe?: string;
	otherEntityId?: string | null;
	locale?: string;
}) {
	const t = useT();
	const ask = { recipe, other_entity_id: otherEntityId ?? undefined, locale };
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "gap-brief", gapId, ask],
		queryFn: () => api.gapBrief(gapId, ask),
		...read,
	});
	return (
		<Async state={state}>
			{(brief) =>
				brief.blocked ? (
					<p className="hint warn" role="status">
						{t(brief.blocked.message_key, brief.blocked.params)}
					</p>
				) : brief.facts.length === 0 ? (
					<EmptyState size="compact" title={t("briefs.noFacts")} description={t("briefs.noFactsBody")} />
				) : (
					<>
						{/* The plain table, not the resource one: this is drawn inside the
						    composer and a dialog, and the resource table's minimum
						    column width pushed the value column out of both. */}
						<div className="table-scroll">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead className="wrap">{t("briefs.fact")}</TableHead>
										<TableHead className="wrap">{t("briefs.value")}</TableHead>
										<TableHead>{t("briefs.source")}</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{brief.facts.map((fact) => (
										<TableRow key={fact.key}>
											<TableCell className="wrap mono">{fact.key}</TableCell>
											<TableCell className="wrap">{fact.value}</TableCell>
											<TableCell>
												{fact.source_url ? (
													<a href={fact.source_url} target="_blank" rel="noreferrer nofollow">
														{t("briefs.hasSource")}
													</a>
												) : (
													// A fact with no source is a fact the page may not lean on.
													<span className="warn">{t("briefs.noSource")}</span>
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
						<p className="faint">{t("briefs.sources", { n: brief.sources.length })}</p>
					</>
				)
			}
		</Async>
	);
}
